import React from 'react';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { richTextToPlain } from '../../../components/editor/richText';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTaskChange } from '../../../stores/KanbanTaskStores';

const { Text } = Typography;

export type TaskChangesProps = {
  store: TaskStore;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;

/** `KanbanTaskChange` carries no user info (see `KanbanTaskApiClient.getTaskChanges`), so this is
 * Date/Field/From/To — the same four columns the inline table it replaces showed. */
const PROPERTY_LABEL_KEYS: Record<string, string> = {
  Title: 'Title',
  Priority: 'Priority',
  StartDate: 'StartDate',
  DueDate: 'DueDate',
  StatusId: 'Status',
  Status: 'Status',
  AssigneeId: 'Assignee',
  Assignee: 'Assignee',
  AuthorId: 'Author',
  Author: 'Author',
  Description: 'Description',
};

function formatValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  if (ISO_DATE.test(value)) {
    return dayjs(value).format('LLL');
  }
  // Handles plain text, HTML and legacy Editor.js JSON alike; harmless no-op on a short plain value.
  return richTextToPlain(value, 80);
}

/** The drawer's Changes tab — port of the inline `<table>` as an antd `Table`. */
const TaskChanges: React.FC<TaskChangesProps> = observer(({ store }) => {
  const { t } = useTranslation();
  const changes = store.taskChanges;

  if (!changes.length) {
    return (
      <Text type="secondary" className="task-changes__empty">
        {t('NoChangesRecorded')}
      </Text>
    );
  }

  const columns: ColumnsType<KanbanTaskChange> = [
    {
      title: t('Date'),
      dataIndex: 'createdDate',
      key: 'date',
      width: 170,
      render: (value: KanbanTaskChange['createdDate']) => dayjs(value).format('LLL'),
    },
    {
      title: t('Property'),
      dataIndex: 'propertyName',
      key: 'property',
      width: 140,
      render: (value?: string) => (value ? t(PROPERTY_LABEL_KEYS[value] ?? value) : ''),
    },
    {
      title: t('OldValue'),
      dataIndex: 'previousValue',
      key: 'previousValue',
      render: (value?: string) => <span className="task-changes__value">{formatValue(value)}</span>,
    },
    {
      title: t('NewValue'),
      dataIndex: 'newValue',
      key: 'newValue',
      render: (value?: string) => <span className="task-changes__value">{formatValue(value)}</span>,
    },
  ];

  return (
    <Table<KanbanTaskChange>
      className="task-changes__table"
      dataSource={changes}
      columns={columns}
      rowKey="id"
      pagination={false}
      size="small"
    />
  );
});

export default TaskChanges;
